import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import HomeHero from "@/components/site/HomeHero";
import Reveal from "@/components/site/Reveal";
import JourneyTile from "@/components/site/JourneyTile";
import CTASection from "@/components/site/CTASection";
import { usePageMeta } from "@/components/site/SEO";
import { JOURNEYS } from "@/content/journeys";
import { APPROACH_PRINCIPLES, SAFETY_COPY, NAME_INSPIRATION, PEOPLE_INTRO_HOME } from "@/content/site";
import { ASSETS } from "@/content/assets";

export default function Home() {
  usePageMeta(
    null,
    "Wandering Glider Experiences creates place-based learning journeys for schools across India. Learn the world by wandering through it.",
  );

  return (
    <>
      <HomeHero />

      <section className="container-x py-20 sm:py-28 lg:py-32" data-testid="home-introduction">
        <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:gap-20">
          <Reveal>
            <p className="eyebrow mb-5">Hello, wanderer</p>
            <h2 className="max-w-xl text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
              The world is our classroom
            </h2>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-ink/80">
              Wandering Glider Experiences creates place-based learning journeys that spark
              discovery and engagement with diverse landscapes, cultures and ecosystems. Founded
              by educators and explorers, we work with experts across disciplines to craft
              immersive journeys that encourage new ways of seeing, thinking and interacting with
              the world.
            </p>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink/80">
              We see the world as a living classroom: dynamic, hands-on and rooted in real-world
              connection.
            </p>
            <Link
              to="/about"
              data-testid="intro-about-link"
              className="mt-8 inline-flex items-center gap-2 text-base font-medium text-pine underline decoration-pine/40 underline-offset-8 transition-colors hover:decoration-pine"
            >
              More about us
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <aside className="border border-line border-l-2 border-l-brass bg-white p-8 sm:p-10" data-testid="name-inspiration-inset">
              <h3 className="text-xl font-medium tracking-tight">{NAME_INSPIRATION.heading}</h3>
              <p className="mt-4 font-serif text-lg font-normal italic leading-relaxed text-ink/90">
                {NAME_INSPIRATION.lede}
              </p>
              {NAME_INSPIRATION.paragraphs.map((paragraph, i) => (
                <p key={i} className="mt-4 text-base leading-relaxed text-ink/75">
                  {paragraph}
                </p>
              ))}
            </aside>
          </Reveal>
        </div>
      </section>

      <section className="bg-pine text-canvas" data-testid="home-approach-preview">
        <div className="container-x py-20 sm:py-28 lg:py-32">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="mb-5 text-xs font-semibold uppercase tracking-[0.22em] text-[#CFC286]">
                  Our Approach
                </p>
                <h2 className="max-w-xl text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
                  What shapes every journey
                </h2>
              </div>
              <Link
                to="/our-approach"
                data-testid="approach-preview-link"
                className="inline-flex items-center gap-2 text-base font-medium text-canvas underline decoration-canvas/40 underline-offset-8 transition-colors hover:decoration-canvas"
              >
                Explore Our Approach
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-16 grid gap-x-12 gap-y-14 sm:grid-cols-2">
            {APPROACH_PRINCIPLES.map((principle, i) => (
              <Reveal key={principle.number} delay={i * 0.08}>
                <div className="border-t border-canvas/20 pt-8">
                  <span className="font-serif text-5xl font-medium text-[#CFC286]" aria-hidden="true">
                    {principle.number}
                  </span>
                  <h3 className="mt-5 text-xl font-medium tracking-tight sm:text-2xl">
                    {principle.title}
                  </h3>
                  <p className="mt-4 max-w-md text-base leading-relaxed text-canvas/80">
                    {principle.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-20 sm:py-28 lg:py-32" data-testid="home-featured-journeys">
        <Reveal>
          <p className="eyebrow mb-5">Journeys</p>
          <h2 className="max-w-2xl text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
            A few places to begin
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-12 md:grid-cols-2 lg:gap-16">
          {JOURNEYS.map((journey, i) => (
            <JourneyTile key={journey.slug} journey={journey} delay={i * 0.1} />
          ))}
        </div>
        <Reveal>
          <div className="mt-16 text-center">
            <Link
              to="/journeys"
              data-testid="explore-all-journeys-cta"
              className="inline-flex items-center gap-2 border border-pine px-8 py-4 text-base font-medium text-pine transition-colors duration-300 hover:bg-pine hover:text-canvas"
            >
              Explore All Journeys
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
      </section>

      <section className="container-x py-20 sm:py-28 lg:py-32" data-testid="home-safety">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <p className="mb-5 text-sm font-semibold uppercase tracking-[0.22em] text-[#7C6E33]">
              {SAFETY_COPY.heading}
            </p>
            <h2 className="max-w-lg text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
              Challenged, supported, safe
            </h2>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-ink/80">{SAFETY_COPY.home}</p>
            <Link
              to="/our-approach#safety"
              data-testid="safety-approach-link"
              className="mt-8 inline-flex items-center gap-2 text-base font-medium text-pine underline decoration-pine/40 underline-offset-8 transition-colors hover:decoration-pine"
            >
              More about safety and our approach
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <img
              src={ASSETS.safetyImage.src}
              alt={ASSETS.safetyImage.alt}
              loading="lazy"
              className="aspect-[4/3] w-full rounded-2xl object-cover"
            />
          </Reveal>
        </div>
      </section>

      <section className="border-t border-line bg-white" data-testid="home-people">
        <div className="container-x grid items-center gap-12 py-20 sm:py-28 lg:grid-cols-2 lg:gap-20 lg:py-32">
          <Reveal>
            <img
              src={ASSETS.peopleField.src}
              alt={ASSETS.peopleField.alt}
              loading="lazy"
              className="aspect-[4/3] w-full rounded-2xl object-cover"
            />
          </Reveal>
          <Reveal delay={0.15}>
            <p className="eyebrow mb-5">Our People</p>
            <h2 className="max-w-lg text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
              The people behind the journeys
            </h2>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-ink/80">{PEOPLE_INTRO_HOME}</p>
            <Link
              to="/our-people"
              data-testid="meet-our-people-cta"
              className="mt-8 inline-flex items-center gap-2 border border-pine px-7 py-3.5 text-base font-medium text-pine transition-colors duration-300 hover:bg-pine hover:text-canvas"
            >
              Meet Our People
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-pineink text-canvas" data-testid="home-closing">
        <div className="container-x grid gap-14 py-20 sm:py-28 lg:grid-cols-[1.3fr_1fr] lg:gap-20 lg:py-32">
          <Reveal>
            <h2 className="max-w-xl text-4xl font-medium leading-[1.1] tracking-tight sm:text-5xl">
              Where will your curiosity take you?
            </h2>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-canvas/80">
              Tell us about your school, your students and what you are curious to explore. We
              will help you find, or shape, the right journey.
            </p>
            <div className="mt-10">
              <Link
                to="/start-your-journey"
                data-testid="closing-start-your-journey-cta"
                className="inline-flex items-center gap-2 bg-canvas px-8 py-4 text-base font-medium text-pineink transition-colors duration-300 hover:bg-white"
              >
                Start Your Journey
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <aside className="border border-canvas/20 p-8 sm:p-10" data-testid="bespoke-preview">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#CFC286]">
                Coming Soon
              </p>
              <h3 className="mt-4 text-2xl font-medium tracking-tight">Bespoke Experiences</h3>
              <p className="mt-4 text-base leading-relaxed text-canvas/75">
                For families, individuals and private, adult and corporate groups: journeys shaped
                around your curiosity, your place, your pace and your purpose.
              </p>
              <Link
                to="/journeys/bespoke-experiences"
                data-testid="bespoke-preview-link"
                className="mt-6 inline-flex items-center gap-2 text-base font-medium text-canvas underline decoration-canvas/40 underline-offset-8 transition-colors hover:decoration-canvas"
              >
                Explore Bespoke Experiences
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </aside>
          </Reveal>
        </div>
      </section>
    </>
  );
}
